import React, { useState, useRef, useEffect } from 'react';
import './FileUpload.scss';
import { useTranslation } from 'react-i18next';
import { ISelectOptions } from 'aw-combobox';
import { IToastProps, NotificationToast } from 'aw-notificationtoast';
import { Form } from 'react-bootstrap';
import { AWSelect } from 'aw-select';
import { useTheme } from '../../utils/contexts/ThemeContext';

interface FileUploadProps {
    loading: boolean
    selectedInstallation?: ISelectOptions
    selectedMachine?: ISelectOptions
    uploadFileClick: () => void;
    setFileName: (name: string) => void;
    setCategory: (category: string) => void;
    setVersion: (version: string) => void
  }

const FileUploadComponent: React.FC<FileUploadProps> = (props) => {
    const { t } = useTranslation();
    const categories = [{ label: t('Manuals'), value: 0 },{ label: t('Guides'), value: 1 }, { label:  t('Errors'), value: 2 },{ label: t('Others'), value: 3 }];
    
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState('');
    const [category, setCategory] = useState(categories[0].label);
    const [version, setVersion] = useState ('1.0')
    const [toast, setToast] = useState<IToastProps>({ showToast: false, msg: '', fail: false })
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const theme = useTheme();

    const dark = theme.theme==='dark';

    // Gestió events del drag and drop
    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) {
            setIsDragging(true);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFile = e.dataTransfer.files[0];
            handleFileChange(droppedFile);
        }
    };

    const handleFileChange = (selectedFile: File) => {
        const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
        if (fileExtension && ['pdf'].includes(fileExtension)) {
            setFile(selectedFile);
            setFileName(selectedFile.name);
        } else {
            showToastTimeOut(t('NoCorrectFormat'), true)
        }
    };

    const handleSelectFile = () => {
        fileInputRef.current?.click();
    };

    const handleInputFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileChange(e.target.files[0]);
        }
    };

    const handleCategoryChange = (selectedOption: ISelectOptions | null)=>{
        if (selectedOption) {
            setCategory(selectedOption.label)
        }
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!file) {
            showToastTimeOut(t('FileMissing'), true)
            return;
        }

        if (!fileName.trim()) {
            showToastTimeOut(t('FileNameMissing'), true)
            return;
        }

        if (!category) {
            showToastTimeOut(t('CategoryMissing'), true)
            return;
        }

        // Pujar a aws
        console.log('Pujar arxiu:', {
            file,
            fileName,
            category
        });

        props.uploadFileClick()
        props.setCategory(category)
        props.setFileName(fileName)
        props.setVersion(version)
        
        
        // Reset del formulari
        setFile(null);
        setFileName('');
        setCategory(categories[0].label);
        setVersion('1.0')
    };

    const showToastTimeOut = (msg: string, failToast: boolean) => {
        setToast({ showToast: true, msg: msg, fail: failToast })
        setTimeout(() => setToast({ showToast: false, msg: msg, fail: failToast }), 2000)
    }

    return (
        <>
        <NotificationToast showToast={toast.showToast} msg={toast.msg} fail={toast.fail} />
        <div id="file-upload-container" className="text-center rounded-1 p-3">
            <div className="row mx-0"> 
                <div className="col-12 mx-auto"> 
                    <span className="mt-1 h4 text-center">{t('FileUpload')}</span>
                    
                    <form className="mt-2" onSubmit={handleSubmit}>
                        <div className="row mx-0"> 
                            <div className="col-12 col-md-4 mb-3 mb-md-0 px-2"> 
                                <p className="form-label text-start text-truncate">{t('FileName')}</p> 
                                <Form.Control type="text" id="fileName" value={fileName} onChange={(e) => setFileName(e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4 mb-3 mb-md-0 px-2"> 
                                <p className="form-label text-start text-truncate">{t('Version')}</p> 
                                <Form.Control
                                    type="text" 
                                    id="version" 
                                    value={version} 
                                    onChange={(e) => setVersion(e.target.value)} 
                                />
                            </div>
                            <div className="col-12 col-md-4 px-2"> 
                                <p className="form-label text-start">{t('Category')}</p> 
                                <AWSelect dark={dark} className='text-start' closeMenuOnSelect={true} defaultValue={categories[0]} onChange={handleCategoryChange} options={categories} />
                            </div>
                        </div>
                        <div className="row mx-0 mt-2"> 
                            <div className='col-12 col-md-4 px-2'>
                            <p className="form-label text-start">{t('Installation')}</p> 
                            <Form.Control
                                    type="text" 
                                    id="installation" 
                                    value={props.selectedInstallation?.label} 
                                    className="form-control text-input" 
                                    placeholder={t('Installation')} 
                                    disabled={true}
                                />
                            </div>
                            <div className='col-12 col-md-4 px-2'>
                            <p className="form-label text-start">{t('Machine')}</p> 
                            <Form.Control
                                    type="text" 
                                    id="machine" 
                                    value={props.selectedMachine?.label} 
                                    className="form-control inputStyle" 
                                    placeholder={t('Machine')} 
                                    disabled={true}
                                />
                                {/* <Form.Control disabled={true} name="log" type="text" className="inputStyle" value={containerAux.lng} /> */}
                            </div>
                        </div>

                        <div className="row mx-0 mt-4"> 
                            <div className="col-12 px-2"> 
                                <div
                                    className={`drag-drop-area ${isDragging ? 'dragging' : ''}`}
                                    onDragEnter={handleDragEnter}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={handleSelectFile}
                                >
                                    <input type="file" ref={fileInputRef} onChange={handleInputFileChange} className="file-input" accept=".pdf,.png,.csv" />
                                    <div className="upload-icon">
                                        <i className="bi bi-cloud-arrow-up"></i>
                                    </div>
                                    <button type="button" className="btn select-file-btn"> {t('SelectFile')} </button>
                                    
                                    {file && (
                                        <div className="selected-file my-2">
                                            {t('Selected File')}: {file.name}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="row mx-0 mt-2"> 
                            <div className="col-12 px-2">
                                <div className="supported-formats">
                                    {t('SupportedFormats')}: PDF, PNG, CSV
                                </div>
                            </div>
                        </div>
                        <div className="row mx-0 mt-4"> 
                            <div className="col-12 px-2"> 
                                <button type="submit" className="btn-Custom btn-IDGreen w-100" >
                                    {t('Upload')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        </>
    );
};

export default FileUploadComponent;